import {
	type ComponentType,
	createContext,
	type ReactNode,
	useContext,
} from "react";
import type { FaqContent } from "src/api/faq";

/** Kontrakty slotow: panel deklaruje props, admin (src/admin/slots.tsx) dostarcza komponent. */
export interface OwnDiscussionSlotProps {
	questionId: number;
	discussion: string | null;
	source: string | null;
	/** Skad omowienie (tylko staff): wlasne albo fallback RAG. */
	type?: "own" | "rag";
	/** Przejscie na zakladke RAG (edycja omowienia typu rag odbywa sie tam). */
	onEditRag?: () => void;
	/** Widok omowienia z panelu (do pokazania poza trybem edycji). */
	children: ReactNode;
}

export interface SearchQuestionsSettingsSlotProps {
	revealAnswers: boolean;
	setRevealAnswers: (value: boolean) => void;
}

export interface CommentReplyEditorSlotProps {
	/** Komentarz, na ktory odpowiadamy. */
	comment: { id: number; questionId: number };
	placeholder?: string;
	submitText?: string;
	/** HTML odpowiedzi; sam pokazuje toasty i rzuca przy bledzie. */
	onSubmit: (html: string) => Promise<void>;
}

export interface CommentActionsSlotProps {
	comment: {
		id: number;
		parentId?: number | null;
		/** Encja z panelowego GET komentarzy pod pytaniem (komentarz główny). */
		resolvedAt?: string | null;
		resolvedById?: number | null;
	};
}

export interface QuestionEditorSlotProps {
	question: {
		id: number;
		/** HTML tresci pytania. */
		pytanie: string;
		/** Klucze a..e. */
		answers: Record<string, string>;
		correctAnswer: string;
		isCanceled: boolean;
	};
	/** Otworz od razu w trybie edycji. */
	startInEdit?: boolean;
	/** Widok pytania z odpowiedziami (do pokazania poza trybem edycji). */
	children: ReactNode;
}

export interface GuideSectionSlotProps {
	section: FaqContent;
	/** Widok sekcji poradnika (do pokazania poza trybem edycji). */
	children: ReactNode;
}

export interface GuideContentsSlotProps {
	sections: FaqContent[];
	renderLink: (section: FaqContent, index: number) => ReactNode;
	/** Zwykla lista spisu tresci (gdy brak uprawnien do zapisu). */
	children: ReactNode;
}

export interface RagDiscussionSlotProps {
	questionId: number;
	/** Otworz od razu w trybie edycji. */
	startInEdit?: boolean;
}

export interface Slots {
	ownDiscussion: ComponentType<OwnDiscussionSlotProps>;
	searchQuestionsSettings: ComponentType<SearchQuestionsSettingsSlotProps>;
	commentReplyEditor: ComponentType<CommentReplyEditorSlotProps>;
	/** Akcje przy komentarzu (admin: oznacz jako rozwiązany). */
	commentActions: ComponentType<CommentActionsSlotProps>;
	/** Akcje obok tabow w /szukaj/artykulow (admin: dodaj artykul). */
	articlesSearchActions: ComponentType;
	/** Akcje obok tabow w /szukaj/zestawow (admin: dodaj zestawy). */
	decksSearchActions: ComponentType;
	/** Karta pytania w wyszukiwarce (admin: edycja tresci, odpowiedzi i poprawnej). */
	questionEditor: ComponentType<QuestionEditorSlotProps>;
	/** Sekcja poradnika (admin: tytul + edycja). */
	guideSection: ComponentType<GuideSectionSlotProps>;
	/** Spis tresci poradnika (admin: kolejnosc, usuwanie, dodawanie). */
	guideContents: ComponentType<GuideContentsSlotProps>;
	/** Zakladka "Omówienie RAG" w omowieniu pytania (admin, PES). */
	ragDiscussion: ComponentType<RagDiscussionSlotProps>;
}

const SlotsContext = createContext<Partial<Slots>>({});

/** Klient nie ustawia providera, wiec kazdy slot jest pusty. */
export const SlotsProvider = SlotsContext.Provider;

export function useSlot<K extends keyof Slots>(name: K): Partial<Slots>[K] {
	return useContext(SlotsContext)[name];
}
